/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel, OptionType } from '@/shared/api/BaseAPI.model'
import { useMemo } from 'react'
import { t } from 'i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import {
  languagesListPayloadModel,
  languagesListResponseModel,
  QUERY
} from '../api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '../api/SidebarAccordionAPI'

export const useSelectedLanguagesQuery = (currentLanguage: string) => {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const selectedLanguagesListQuery = useQuery<
    languagesListResponseModel,
    ErrorModel
  >({
    queryKey: [QUERY.SELECTED_LANGUAGES],
    queryFn: SidebarAccordionAPI.getSelectedLangauges
  })

  const selectedLanguagesList = currentLanguage
    ? selectedLanguagesListQuery.data?.[
        currentLanguage as keyof languagesListResponseModel
      ] || []
    : []

  const defaultLanguage = selectedLanguagesListQuery.data?.[
    currentLanguage as keyof languagesListResponseModel
  ].find(language => language.isDefault === true)

  const defaultLanguageOption = useMemo(
    () => ({
      label: defaultLanguage?.name || '',
      value: defaultLanguage?.languageCode || ''
    }),
    [defaultLanguage]
  ) as OptionType

  const selectedLanguagesOptions = useMemo(
    () =>
      selectedLanguagesListQuery.data?.[
        currentLanguage as keyof languagesListResponseModel
      ].map(language => ({
        label: language.name,
        value: language.languageCode
      })) || [],

    [currentLanguage, selectedLanguagesListQuery.data]
  ) as OptionType[]

  const { mutate: putLanguages } = useMutation<
    languagesListPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (languageList: languagesListPayloadModel) =>
      SidebarAccordionAPI.putLanguages(languageList),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY.SELECTED_LANGUAGES]
      })

      showSnackbar(
        t('ALERTS.PUT_LANGUAGES_SUCCESS.TITLE'),
        t('ALERTS.PUT_LANGUAGES_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_LANGUAGES_ERROR.TITLE'),
        t('ALERTS.PUT_LANGUAGES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    selectedLanguagesListQuery,
    selectedLanguagesList,
    selectedLanguagesOptions,
    defaultLanguage,
    defaultLanguageOption,
    putLanguages
  }
}
